'use client'

/**
 * Step progress for the shared step views: "Mark step N done" checkboxes,
 * per-chapter "x of n done", and the header "Resume · N/M" button.
 *
 * Flogger chapter-model migration PR-2b (design doc
 * `docs/design/flogger-components-chapter-model-design.md`, section 3,
 * "Step progress (PR-2b), specified"). Two switches, both required:
 *   1. the PAGE makes it available: `ArticleView stepProgress={{ docId:
 *      <Sanity _id> }}` (PR-3 passes it for the flogger component types
 *      only). Without it the context is the inert default below: no
 *      checkbox, no count, no Resume, no "Track my steps" switch and no
 *      storage access, so tutorials, scenes and lessons render and behave
 *      exactly as before;
 *   2. the READER turns it on: the "Track my steps" switch next to the
 *      List / Grid / Carousel picker (or Account -> Preferences). OFF by
 *      default for everyone (owner decision 2026-11-04). While it is off the
 *      page shows only that switch: no checkbox, no count, no Resume, no
 *      `aria-describedby` ids, and `step-progress:<_id>` is not read (stored
 *      ticks are kept; turning tracking back on shows them again).
 *      The choice is `useStepTrackingPreference` (account value when signed
 *      in, else localStorage `theduchy.step-tracking`), held by ArticleView
 *      and passed in as `tracking` / `onTrackingChange`.
 *
 * Why a context: the `stepByStepGrid` block is created by
 * `PortableTextRenderer`, so a prop cannot reach it (design doc section 3).
 *
 * Persistence: localStorage `step-progress:<_id>`, a JSON array of done step
 * `_key`s (see `src/lib/content/step-progress.ts`). Every access is in
 * try/catch. The server and the first client render show every step unticked;
 * the stored set is read after mount, so there is no hydration mismatch.
 * Writes happen only when the reader ticks or unticks a step (never on load).
 * Another tab's change to the same key is picked up via the `storage` event.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { flushSync } from 'react-dom'
import { useTranslations } from 'next-intl'
import {
  countAllDoneSteps,
  countDoneSteps,
  findFirstUndoneStep,
  parseStepProgress,
  parseStepTracking,
  resolveInitialStepTracking,
  serializeStepProgress,
  serializeStepTracking,
  shouldShowResume,
  STEP_TRACKING_STORAGE_KEY,
  stepProgressStorageKey,
  withStepDone,
  type ChapterSteps,
  type DoneCount,
} from '@/lib/content/step-progress'

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

/** Sets a mounted carousel's index (registered by a `stepByStepGrid` block). */
type CarouselSetter = (index: number) => void

/**
 * The stable half of the context: whether progress is on, and the actions.
 * Its identity does not change when a step is ticked, so the `stepByStepGrid`
 * blocks (which read only this half) do not re-render on every tick.
 */
export interface StepProgressConfig {
  /** True when the page offers step progress (the provider has a `docId`).
   *  Shows the "Track my steps" switch; says nothing about the reader. */
  available: boolean
  /** The reader's "Track my steps" choice (always false when unavailable). */
  tracking: boolean
  /** Turn "Track my steps" on or off (no-op when unavailable). */
  setTracking: (on: boolean) => void
  /** Progress is showing: `available && tracking`. False outside a provider,
   *  without a `docId`, or while the reader has tracking off. */
  enabled: boolean
  setDone: (stepKey: string, done: boolean) => void
  /** Jump to the first unfinished step (see `StepProgressProvider`). */
  resume: () => void
  /**
   * A `stepByStepGrid` block in carousel mode registers its index setter
   * under its block `_key`, so Resume can move it. Returns the unregister.
   */
  registerCarousel: (gridKey: string, setIndex: CarouselSetter) => () => void
}

/**
 * The changing half: the done set and the counts. Read only by the small
 * pieces that show them (checkbox, chapter count, Resume button).
 */
export interface StepProgressState {
  /** True once the stored set has been read (after mount). */
  hydrated: boolean
  isDone: (stepKey: string) => boolean
  /** Done / total for one chapter section; null when it tracks no steps. */
  chapterCount: (sectionId: string) => DoneCount | null
  /** Done / total across every chapter. */
  totalCount: DoneCount
}

export type StepProgressContextValue = StepProgressConfig & StepProgressState

const ZERO: DoneCount = Object.freeze({ done: 0, total: 0 }) as DoneCount
const noop = () => {}

const DISABLED_CONFIG: StepProgressConfig = Object.freeze({
  available: false,
  tracking: false,
  setTracking: noop,
  enabled: false,
  setDone: noop,
  resume: noop,
  registerCarousel: () => noop,
})

const DISABLED_STATE: StepProgressState = Object.freeze({
  hydrated: false,
  isDone: () => false,
  chapterCount: () => null,
  totalCount: ZERO,
})

const StepProgressConfigContext = createContext<StepProgressConfig>(DISABLED_CONFIG)
const StepProgressStateContext = createContext<StepProgressState>(DISABLED_STATE)

/**
 * The stable half only (on/off + actions). Use this where the done set is not
 * shown, so ticking a step does not re-render the caller.
 */
export function useStepProgressConfig(): StepProgressConfig {
  return useContext(StepProgressConfigContext)
}

/** Both halves (re-renders on every tick while progress is on). */
export function useStepProgress(): StepProgressContextValue {
  const config = useContext(StepProgressConfigContext)
  const state = useContext(StepProgressStateContext)
  return { ...config, ...state }
}

/** Done / total for one chapter, or null (progress off, or no tracked steps). */
export function useChapterStepProgress(sectionId: string): DoneCount | null {
  const { enabled } = useContext(StepProgressConfigContext)
  const state = useContext(StepProgressStateContext)
  return enabled ? state.chapterCount(sectionId) : null
}

/**
 * Turns step progress off for a subtree: the inert default contexts, so a
 * step block inside gets no checkbox and no "Track my steps" switch.
 * ArticleView wraps its extra (non-chapter) sections in it (PR-2c), since
 * only chapters are tracked: a step there would otherwise show a checkbox
 * that no count or Resume knows about.
 */
export function StepProgressOff({ children }: { children: ReactNode }) {
  return (
    <StepProgressConfigContext.Provider value={DISABLED_CONFIG}>
      <StepProgressStateContext.Provider value={DISABLED_STATE}>{children}</StepProgressStateContext.Provider>
    </StepProgressConfigContext.Provider>
  )
}

/**
 * The id of the chapter heading a step block sits under, set by ArticleView
 * around each chapter's content while progress is on. The step checkboxes
 * point `aria-describedby` at it (and at the block's own title), so the many
 * "Mark step 1 done" checkboxes on a page (one grid per chapter) can be told
 * apart. Null → no chapter context.
 */
const StepProgressChapterContext = createContext<string | null>(null)

export function StepProgressChapter({
  headingId,
  children,
}: {
  headingId: string | null
  children: ReactNode
}) {
  return (
    <StepProgressChapterContext.Provider value={headingId}>{children}</StepProgressChapterContext.Provider>
  )
}

/* ------------------------------------------------------------------ */
/*  DOM helpers (Resume)                                               */
/* ------------------------------------------------------------------ */

/** Matches ArticleView's scroll offset (clears the sticky site header). */
const SCROLL_OFFSET = 96

/** Readers who ask for reduced motion get an instant jump. The site-wide CSS
 *  guard only resets `scroll-behavior`; an explicit `behavior: 'smooth'` in
 *  JS would override it, so this checks the media query itself. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  } catch {
    return false
  }
}

/** Margin kept below `keepVisible` when it has to be pulled into view. */
const KEEP_VISIBLE_MARGIN = 8

/**
 * Scroll so `el` sits just under the sticky header. When `keepVisible` (the
 * checkbox Resume is about to focus) would land below the fold — a tall step
 * photo on a short or phone-sized viewport puts it there — scroll further so
 * it is on screen: focus must never land on an off-screen control (WCAG
 * 2.4.11 Focus Not Obscured).
 */
function scrollToElement(el: Element, keepVisible?: Element | null) {
  let top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET
  if (keepVisible) {
    const bottom = keepVisible.getBoundingClientRect().bottom + window.scrollY + KEEP_VISIBLE_MARGIN
    if (bottom - top > window.innerHeight) top = bottom - window.innerHeight
  }
  window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
}

/**
 * Open every closed `<details>` around `el` (an accordion panel holding a
 * step grid starts closed). Content of a closed `<details>` has no layout, so
 * it could be neither scrolled to nor focused.
 */
function revealElement(el: Element) {
  for (let d = el.closest('details'); d; d = d.parentElement?.closest('details') ?? null) {
    if (!d.open) d.open = true
  }
}

/**
 * Focus `el` without scrolling again. A non-focusable element (a heading)
 * gets `tabindex="-1"` first so it can take programmatic focus.
 */
function focusQuietly(el: Element | null) {
  if (!(el instanceof HTMLElement)) return
  if (!el.matches('input, button, a[href], select, textarea, [tabindex]')) {
    el.setAttribute('tabindex', '-1')
  }
  el.focus({ preventScroll: true })
}

/** Where focus lands when Resume goes to a chapter rather than a step: its
 *  first step checkbox that is not in a closed panel, else its heading, else
 *  the chapter itself. */
function chapterFocusTarget(chapter: Element): Element {
  for (const box of chapter.querySelectorAll('input[data-step-progress-key]')) {
    if (!box.closest('details:not([open])')) return box
  }
  return chapter.querySelector('h2, h3') ?? chapter
}

/** The rendered checkbox for a step, if that step is on screen. Attribute
 *  values are compared directly (no selector escaping needed). */
function findStepCheckbox(stepKey: string): HTMLInputElement | null {
  const all = document.querySelectorAll<HTMLInputElement>('input[data-step-progress-key]')
  for (const el of all) if (el.getAttribute('data-step-progress-key') === stepKey) return el
  return null
}

/* ------------------------------------------------------------------ */
/*  "Track my steps" preference                                        */
/* ------------------------------------------------------------------ */

/**
 * The reader's "Track my steps" choice, the same way the Default Step View
 * preference works (`StepViewProvider`):
 *   - first render (server and client alike): the account value when the
 *     server read one (`initialTracking`), else OFF, so there is no
 *     hydration mismatch;
 *   - after mount, with no account value (signed out, a member who never
 *     chose, since `profiles.step_tracking_enabled` is nullable like
 *     `step_view_default`, or the column not readable yet): the browser's
 *     saved choice, if any. So a choice made while signed out still applies
 *     after sign-in until the member saves an account value;
 *   - a change saves to localStorage and, when signed in, to the account
 *     (PUT /api/account/step-view `{ tracking }`, fire-and-forget).
 * `available` false (the page does not offer progress) → nothing is read.
 * Owner decision 2026-11-04: off by default for everyone.
 */
export function useStepTrackingPreference({
  available,
  initialTracking = null,
  authed = false,
}: {
  available: boolean
  /** `profiles.step_tracking_enabled` read on the server; null → never
   *  chosen / signed out / unreadable (the browser's choice applies). */
  initialTracking?: boolean | null
  authed?: boolean
}): readonly [boolean, (on: boolean) => void] {
  const [tracking, setTrackingState] = useState(() => resolveInitialStepTracking(initialTracking))

  useEffect(() => {
    if (!available || initialTracking != null) return
    let stored: boolean | null = null
    try {
      stored = parseStepTracking(localStorage.getItem(STEP_TRACKING_STORAGE_KEY))
    } catch {
      // localStorage unavailable (private mode, blocked storage)
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- apply the browser's saved choice after mount (a render-time read would mismatch SSR)
    if (stored !== null) setTrackingState(stored)
  }, [available, initialTracking])

  const setTracking = useCallback(
    (on: boolean) => {
      setTrackingState(on)
      try {
        localStorage.setItem(STEP_TRACKING_STORAGE_KEY, serializeStepTracking(on))
      } catch {
        // localStorage unavailable: the choice still holds for this visit
      }
      if (authed) {
        fetch('/api/account/step-view', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tracking: on }),
        }).catch(() => {
          /* network down, server error: local state still updated */
        })
      }
    },
    [authed],
  )

  return [tracking, setTracking] as const
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export interface StepProgressProviderProps {
  children: ReactNode
  /**
   * Sanity `_id` of the document. Truthy → the page offers step progress
   * (the "Track my steps" switch shows). Falsy → the inert default context:
   * no switch, nothing read or written.
   */
  docId?: string | null
  /**
   * The reader's "Track my steps" choice (`useStepTrackingPreference`).
   * Progress shows only when this is true AND `docId` is set. Default off.
   */
  tracking?: boolean
  /** Called by the "Track my steps" switch. */
  onTrackingChange?: (on: boolean) => void
  /**
   * Tracked steps per chapter, in reading order
   * (`buildChapterStepIndex(sections)`). Drives the counts and Resume.
   */
  chapters: readonly ChapterSteps[]
  /**
   * The element to scroll to for a chapter (ArticleView passes its section
   * refs). Used by Resume in carousel mode, and when every step is done.
   */
  getChapterElement?: (sectionId: string) => Element | null
}

/**
 * Resume, per the design doc:
 *   - list or grid: scroll to the first undone step's node (its row / card);
 *   - carousel: set that block's carousel to the step (`current`) and scroll
 *     to its chapter.
 * In both cases focus then moves to that step's checkbox (without scrolling
 * again), so keyboard and screen-reader users land on the step too. When
 * every step is done it scrolls to the first chapter that has steps (the
 * bench scrolled back to its step pane).
 *
 * Mode is decided per block, not read from the page preference: a block that
 * registered a carousel setter is in carousel mode; any other block renders
 * every step, so its checkbox is in the DOM.
 */
export function StepProgressProvider({
  children,
  docId,
  tracking = false,
  onTrackingChange,
  chapters,
  getChapterElement,
}: StepProgressProviderProps) {
  const available = typeof docId === 'string' && docId !== ''
  // Off until the reader turns tracking on: no key → no storage read/write.
  const enabled = available && tracking
  const storageKey = enabled ? stepProgressStorageKey(docId) : null

  const [done, setDoneState] = useState<ReadonlySet<string>>(() => new Set())
  const [hydrated, setHydrated] = useState(false)
  // Mirror for event handlers (toggle + Resume read the latest set).
  const doneRef = useRef<ReadonlySet<string>>(done)

  // Read the stored set after mount (render-time reads would mismatch SSR),
  // and follow other tabs' changes to the same key.
  useEffect(() => {
    if (!storageKey) return
    const load = () => {
      let raw: string | null = null
      try {
        raw = localStorage.getItem(storageKey)
      } catch {
        // localStorage unavailable (private mode, blocked storage)
      }
      const next = parseStepProgress(raw)
      doneRef.current = next
      setDoneState(next)
    }
    load()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mark hydrated after the post-mount storage read (a render-time read would mismatch SSR)
    setHydrated(true)
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey || e.key === null) load()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [storageKey])

  const setDone = useCallback(
    (stepKey: string, value: boolean) => {
      if (!storageKey) return
      const next = withStepDone(doneRef.current, stepKey, value)
      doneRef.current = next
      setDoneState(next)
      try {
        const serialized = serializeStepProgress(next)
        if (serialized === null) localStorage.removeItem(storageKey)
        else localStorage.setItem(storageKey, serialized)
      } catch {
        // localStorage unavailable: the tick still holds for this visit
      }
    },
    [storageKey],
  )

  const carousels = useRef(new Map<string, CarouselSetter>())
  const registerCarousel = useCallback((gridKey: string, setIndex: CarouselSetter) => {
    carousels.current.set(gridKey, setIndex)
    return () => {
      if (carousels.current.get(gridKey) === setIndex) carousels.current.delete(gridKey)
    }
  }, [])

  const countsBySection = useMemo(() => {
    const m = new Map<string, DoneCount>()
    for (const c of chapters) if (c.steps.length > 0) m.set(c.sectionId, countDoneSteps(c.steps, done))
    return m
  }, [chapters, done])
  const totalCount = useMemo(() => countAllDoneSteps(chapters, done), [chapters, done])

  const resume = useCallback(() => {
    const target = findFirstUndoneStep(chapters, doneRef.current)
    const chapterEl = (id: string) => getChapterElement?.(id) ?? null
    // Scroll to a chapter and move focus into it (all done, or the step's
    // checkbox is not on the page), so keyboard and screen-reader users land
    // where the page went instead of staying on the header button.
    const goToChapter = (id: string) => {
      const el = chapterEl(id)
      if (!el) return
      scrollToElement(el)
      focusQuietly(chapterFocusTarget(el))
    }
    if (!target) {
      const first = chapters.find((c) => c.steps.length > 0)
      if (first) goToChapter(first.sectionId)
      return
    }
    const setCarousel = target.gridKey ? carousels.current.get(target.gridKey) : undefined
    if (setCarousel) {
      // Commit the new carousel step now so its checkbox exists below.
      flushSync(() => setCarousel(target.index))
      const box = findStepCheckbox(target.key)
      if (box) revealElement(box)
      const el = chapterEl(target.sectionId) ?? box
      if (el) scrollToElement(el, box)
      box?.focus({ preventScroll: true })
      return
    }
    const box = findStepCheckbox(target.key)
    if (box) {
      // Open a closed accordion panel first: its content has no layout.
      revealElement(box)
      scrollToElement(box.closest('[data-step-key]') ?? box, box)
      box.focus({ preventScroll: true })
      return
    }
    goToChapter(target.sectionId)
  }, [chapters, getChapterElement])

  const setTracking = useCallback((on: boolean) => onTrackingChange?.(on), [onTrackingChange])

  // Two contexts: `config` keeps its identity across ticks (the step blocks
  // read only it), `state` changes on every tick (checkboxes, counts, Resume).
  const config = useMemo<StepProgressConfig>(
    () =>
      !available
        ? DISABLED_CONFIG
        : enabled
          ? { available: true, tracking: true, setTracking, enabled: true, setDone, resume, registerCarousel }
          : { ...DISABLED_CONFIG, available: true, setTracking },
    [available, enabled, setTracking, setDone, resume, registerCarousel],
  )
  const state = useMemo<StepProgressState>(
    () =>
      enabled
        ? {
            hydrated,
            isDone: (k) => done.has(k),
            chapterCount: (id) => countsBySection.get(id) ?? null,
            totalCount,
          }
        : DISABLED_STATE,
    [enabled, hydrated, done, countsBySection, totalCount],
  )

  return (
    <StepProgressConfigContext.Provider value={config}>
      <StepProgressStateContext.Provider value={state}>{children}</StepProgressStateContext.Provider>
    </StepProgressConfigContext.Provider>
  )
}

/* ------------------------------------------------------------------ */
/*  UI pieces                                                          */
/* ------------------------------------------------------------------ */

/**
 * "Track my steps": the reader's on/off switch for step progress, shown next
 * to the List / Grid / Carousel picker. Renders nothing unless the page
 * offers progress (`docId`), so pages without it gain no control. A
 * `role="switch"` button whose visible text is its accessible name.
 */
export function StepTrackingToggle({ className = '' }: { className?: string }) {
  const { available, tracking, setTracking } = useStepProgressConfig()
  const t = useTranslations('articleView')
  if (!available) return null
  return (
    <button
      type="button"
      role="switch"
      aria-checked={tracking}
      onClick={() => setTracking(!tracking)}
      data-step-tracking-toggle=""
      className={`inline-flex items-center gap-2 px-1 py-1 text-xs font-medium text-gray-600 transition-colors hover:text-gray-900 ${className}`}
    >
      <span
        aria-hidden="true"
        className="relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors"
        style={{ background: tracking ? 'var(--brand-500)' : 'rgba(95,100,122,0.35)' }}
      >
        <span
          className="absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-[left]"
          style={{ left: tracking ? 14 : 2 }}
        />
      </span>
      {t('trackSteps')}
    </button>
  )
}

/**
 * "Mark step N done" — the checkbox slot for a step (list row, grid card,
 * carousel footer). Renders nothing when progress is off. The visible label
 * is the accessible name. `data-step-progress-key` is Resume's hook.
 */
export function StepDoneCheckbox({
  stepKey,
  number,
  describedBy = null,
  className = '',
}: {
  stepKey: string
  /** The number the view draws for this step. */
  number: number
  /**
   * Id of the step block's own title (`<h3>`), when it has one. Together with
   * the chapter heading (`StepProgressChapter`) it becomes the checkbox's
   * `aria-describedby`, so "Mark step 1 done" in two chapters is told apart.
   */
  describedBy?: string | null
  className?: string
}) {
  const ctx = useStepProgress()
  const chapterHeadingId = useContext(StepProgressChapterContext)
  const t = useTranslations('articleView')
  if (!ctx.enabled) return null
  const checked = ctx.isDone(stepKey)
  const description = [describedBy, chapterHeadingId].filter(Boolean).join(' ') || undefined
  return (
    <label
      className={`inline-flex cursor-pointer items-center gap-2 text-[13px] font-medium text-brand-700 ${className}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => ctx.setDone(stepKey, e.currentTarget.checked)}
        aria-describedby={description}
        data-step-progress-key={stepKey}
        className="h-4 w-4 accent-[color:var(--brand-500)]"
      />
      {t('markStepDone', { number })}
    </label>
  )
}

/**
 * Per-chapter "x of n done" with a thin bar (the bench's per-group bar), for
 * a chapter header. Renders nothing when progress is off or the chapter
 * tracks no steps. Server and first client render show "0 of n done"; the
 * stored count appears after mount (same height, so no layout shift).
 */
export function ChapterStepProgress({ sectionId }: { sectionId: string }) {
  const count = useChapterStepProgress(sectionId)
  const t = useTranslations('articleView')
  if (!count || count.total === 0) return null
  const pct = Math.round((count.done / count.total) * 100)
  return (
    <div className="mt-2 max-w-xs" data-chapter-step-progress={sectionId}>
      <div className="h-1 w-full bg-border" aria-hidden="true">
        <div
          className="h-full transition-[width]"
          style={{ width: `${pct}%`, background: 'var(--brand-500)' }}
        />
      </div>
      <p className="mt-1 text-[13px] font-bold tabular-nums text-brand-700">
        {t('chapterStepsDone', { done: count.done, total: count.total })}
      </p>
    </div>
  )
}

/**
 * The header "Resume · N/M" button. Shown only when progress is on, the page
 * tracks steps, and at least one is done (as on the bench); so it never
 * renders on the server or before the stored set is read.
 */
export function StepProgressResumeButton({ className = '' }: { className?: string }) {
  const ctx = useStepProgress()
  const t = useTranslations('articleView')
  if (!ctx.enabled || !ctx.hydrated || !shouldShowResume(ctx.totalCount)) return null
  return (
    <button
      type="button"
      onClick={ctx.resume}
      className={`inline-flex items-center gap-1 px-3 py-1.5 text-[13px] font-bold text-white transition-colors hover:brightness-110 ${className}`}
      style={{ background: 'var(--brand-500)' }}
    >
      {t('resumeSteps', { done: ctx.totalCount.done, total: ctx.totalCount.total })}
      <span aria-hidden="true">→</span>
    </button>
  )
}
